import { ISlOption, TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import { TAddRemoveCgs, canAddArtificialCgs, createCgsOptions } from "./cgsExportOptions";
import {
  isTrue,
  pickAllowed,
  readSavedExportOptions,
  saveExportOptions,
} from "./exportOptionsMemory";

import type { IOvdToNsdOptions } from "../../../../../../../app/services/ovd-nsd-converter/ovdNsdConverter";
// Type-only: brings in the sl-* HTMLElementTagNameMap entries without loading
// Shoelace itself, which specs of this module would otherwise have to transform.
import type { SlDetails } from "@shoelace-style/shoelace";
import { getTranslation } from "../../../../../../../app/i18/i18tn";
import { z } from "zod";

/** Always set here, unlike the converter option it feeds, which is optional. */
type TTrailing40s = NonNullable<IOvdToNsdOptions["trailing40s"]>;

export interface IExportNsdOptions {
  heightFactor: number;
  trailing40s: TTrailing40s;
  showDebug: boolean;
  addRemoveCgs: TAddRemoveCgs;
}

/** Preferences key holding the whole advanced-options object, as the other
 *  "remember what I picked last time" dialogs do (`cgHelpers-copyBayCGs`). */
const NSD_EXPORT_PREF_ID = "nsdExport-options";

/** What is persisted: the advanced field values plus the panel's open state. */
interface INsdExportPrefs extends IExportNsdOptions {
  advancedOpen: boolean;
}

const NSD_EXPORT_DEFAULTS: INsdExportPrefs = {
  addRemoveCgs: "none",
  heightFactor: 0,
  trailing40s: "move",
  showDebug: false,
  advancedOpen: false,
};

/**
 * VCG height factors offered for the NSD export.
 *
 * 0.0 stores bottom bases directly in the NSD instead of mid-container VCGs, and
 * is the default because that is what NSD consumers normally expect. The other
 * two are the usual mid-container factors.
 */
const HEIGHT_FACTOR_OPTIONS: ISlOption[] = [
  { value: 0, label: "0.0" },
  { value: 0.45, label: "0.45" },
  { value: 0.5, label: "0.5" },
];

/**
 * What to do with a trailing 40' pair whose fore half accepts nothing.
 *
 * "move" is the default because it is what Ship Editor's own files do: the 40s
 * go in the fore bay and the 40' partner is still declared.
 */
const TRAILING_40S_OPTIONS: ISlOption[] = [
  { value: "move", label: getTranslation("view:download.nsdTrailing40sMove") },
  { value: "collapse", label: getTranslation("view:download.nsdTrailing40sCollapse") },
  { value: "keep", label: getTranslation("view:download.nsdTrailing40sKeep") },
];

/**
 * The advanced options as the user last exported them.
 *
 * Nothing stored is trusted: localStorage outlives releases, so a saved value can
 * be stale (a heightFactor no longer offered) or inapplicable ("addArtificial" on
 * a file with no missing CGs, where that option is not rendered at all). Each one
 * is checked against the very option list the dialog builds, so the guards cannot
 * drift from the UI.
 *
 * @param cgsOptions the CGs options available for the current file.
 */
export function readSavedNsdExportOptions(cgsOptions: ISlOption[]): INsdExportPrefs {
  const saved = readSavedExportOptions<INsdExportPrefs>(NSD_EXPORT_PREF_ID);

  return {
    addRemoveCgs: pickAllowed(
      saved.addRemoveCgs,
      cgsOptions.map((o) => o.value) as TAddRemoveCgs[],
      NSD_EXPORT_DEFAULTS.addRemoveCgs,
    ),
    heightFactor: pickAllowed(
      saved.heightFactor,
      HEIGHT_FACTOR_OPTIONS.map((o) => o.value) as number[],
      NSD_EXPORT_DEFAULTS.heightFactor,
    ),
    trailing40s: pickAllowed(
      saved.trailing40s,
      TRAILING_40S_OPTIONS.map((o) => o.value) as TTrailing40s[],
      NSD_EXPORT_DEFAULTS.trailing40s,
    ),
    showDebug: isTrue(saved.showDebug),
    advancedOpen: isTrue(saved.advancedOpen),
  };
}

/** Remembers the options for the next export. */
export function saveNsdExportOptions(options: IExportNsdOptions, advancedOpen: boolean): void {
  saveExportOptions<INsdExportPrefs>(NSD_EXPORT_PREF_ID, { ...options, advancedOpen });
}

const FormValidator: z.Schema<IExportNsdOptions> = z.object({
  // sl-select always yields a string, so the chosen factor is coerced back.
  heightFactor: z.custom<number>().transform((value) => Number(value)),
  trailing40s: z.enum(["move", "collapse", "keep"]),
  showDebug: z.boolean(),
  addRemoveCgs: z.enum(["none", "remove", "addArtificial"]),
});

/**
 * Asks for the options needed to export the current OVD as an NSD file.
 *
 * The shipClass token is not asked for: the converter defaults it to the OVD's
 * own shipClass, which is what we want.
 *
 * @returns the chosen options, or `undefined` if the user cancelled the dialog.
 */
export function createNsdExportOptionsDialog(): Promise<IExportNsdOptions | undefined> {
  return new Promise((resolve) => {
    const cgsOptions = createCgsOptions(canAddArtificialCgs());
    const saved = readSavedNsdExportOptions(cgsOptions);

    const dialog = document.createElement("sl-dialog");
    dialog.label = getTranslation("view:download.downloadNSDOptions");

    const btnGo = document.createElement("sl-button");
    btnGo.innerHTML = getTranslation("general:common.export");
    btnGo.slot = "footer";
    btnGo.variant = "primary";
    btnGo.type = "submit";

    const tedivoForm = new TedivoForm<IExportNsdOptions>({
      fields: [
        {
          name: "addRemoveCgs",
          type: "select",
          label: getTranslation("general:exportToStaf.cgsOptions"),
          options: cgsOptions,
          initialValue: saved.addRemoveCgs,
        },
        {
          name: "heightFactor",
          type: "select",
          label: "view:download.nsdHeightFactor",
          helpText: "view:download.nsdHeightFactorHelp",
          options: HEIGHT_FACTOR_OPTIONS,
          initialValue: saved.heightFactor,
        },
        {
          name: "trailing40s",
          type: "select",
          label: "view:download.nsdTrailing40s",
          helpText: "view:download.nsdTrailing40sHelp",
          options: TRAILING_40S_OPTIONS,
          initialValue: saved.trailing40s,
        },
        {
          name: "showDebug",
          type: "checkbox",
          label: "general:common.showDebugOutput",
          initialValue: saved.showDebug,
        },
      ],
      onSubmit: () => null,
      formValidator: FormValidator,
      submitButton: btnGo,
    });

    translateTedivoForm<IExportNsdOptions>({ tedivoForm, getTranslation });

    const advancedOptionsHolder: SlDetails = document.createElement("sl-details");
    advancedOptionsHolder.appendChild(tedivoForm.form);
    advancedOptionsHolder.summary = getTranslation("general:common.advancedOptions");
    advancedOptionsHolder.open = saved.advancedOpen;

    const disclaimerHolder = document.createElement("p");
    disclaimerHolder.innerHTML = getTranslation("view:download.nsdDisclaimer");

    dialog.appendChild(disclaimerHolder);
    dialog.appendChild(advancedOptionsHolder);
    dialog.appendChild(btnGo);

    // Resolve when the user closes with Escape or the X, so callers don't hang
    // waiting for a result that will never come.
    dialog.addEventListener("sl-request-close", () => {
      resolve(undefined);
      dialog.remove();
    });

    btnGo.addEventListener("click", () => {
      const validResult = tedivoForm.doSubmitForm();

      if (!validResult.success) return;

      // Read the panel state before the dialog goes away.
      saveNsdExportOptions(validResult.data, advancedOptionsHolder.open);

      dialog.hide();
      dialog.remove();

      resolve(validResult.data);
    });

    document.body.appendChild(dialog);
    dialog.show();
  });
}
